import { Link } from '@tanstack/react-router';
import type { ConsumerOrderSummary } from '@/entities/order/api';
import { Pill } from '@/shared/ui/Pill';
import { CarBrandLogo } from '@/shared/ui/CarBrandLogo';
import { activeOrderHeadline, orderStatusUi } from './orderStatus';

/**
 * One выезд in progress, as it appears on C1.
 *
 * Everything on this card comes from `/orders/active`, and that response is deliberately thin:
 * no plate, no address, no litres, no price — those live on `GET /orders/{id}`. So the card shows
 * exactly what it has and does not pretend otherwise. Android reserves a corner for a fuel
 * summary that is provably always empty on real data; that corner is not reproduced here.
 *
 * The second line pairs the car's colour with the progress phrase («Белый · оператор едет к вам»).
 * Android puts an address there — the field exists in its DTO and is never filled — while iOS
 * shows only the colour and loses the progress. The colour is what identifies the car to the
 * operator standing next to it, and the phrase is what the person is actually waiting for.
 *
 * The whole card is the link, not a chevron in its corner: this is the one thing on the home
 * screen somebody is trying to reach, and a 40-pixel tap target for it would be a joke on a phone.
 */
export function ActiveOrderCard({ order }: { order: ConsumerOrderSummary }) {
  const status = orderStatusUi(order.tracking_substate);
  const secondary = [order.car_color.trim(), status.detail]
    .filter((part) => part.length > 0)
    .join(' · ');

  return (
    <Link
      to="/order/$orderId"
      params={{ orderId: order.id }}
      className="flex items-center gap-3 rounded-md border border-line bg-surface px-4 py-[13px]"
      style={{ boxShadow: 'var(--shadow-card)' }}
    >
      {/* The make's emblem, same as the car list. `car_brand` can be an empty string when an
          order has no car rows left, and the component answers that with our own mark rather
          than with a gap. */}
      <CarBrandLogo brand={order.car_brand} size={40} />

      {/* `flex-auto`, а не `flex-1`: база колонки — её собственный текст, поэтому ряд честно
          переполняется и плашка справа отдаёт часть ширины. С `flex-1` (база 0) переполнения не
          бывает, плашка держит все свои 128 px, и названию остаётся 98 px из нужных 182. */}
      <div className="min-w-0 flex-auto">
        <h3 className="truncate text-[16px] font-semibold leading-[1.3125]">
          {activeOrderHeadline(order)}
        </h3>
        {secondary.length > 0 && (
          <p className="mt-0.5 truncate text-[12px] leading-[1.3333] text-ink-soft">{secondary}</p>
        )}
      </div>

      {/* Плашка теперь делит нехватку ширины с названием, а не забирает своё целиком (см. `Pill`);
          обёртки вокруг неё больше нет — лишний flex-элемент ломал бы этот дележ, а `aria-live`
          живёт на самой плашке. До этого потолок ширины стоял ТОЛЬКО на нераспознанном коде
          `status.raw`, а знакомое «Ищем оператора» съедало карточку не хуже чужого кода.
          Polite, не assertive: статус меняется под опросом, пока человек читает экран, и
          объявление каждые 25 с было бы своим видом враждебности. */}
      <Pill variant={status.variant} live>
        {status.label}
      </Pill>
    </Link>
  );
}
